import { useEffect, useRef, useState } from "react";
import { api, fmtErr, API, getToken, bytesToHuman } from "../lib/api";
import { useT } from "../i18n";
import {
    DownloadSimple,
    UploadSimple,
    Database,
    HardDrives,
    ImagesSquare,
    FilmStrip,
    QrCode,
    Warning,
    CheckCircle,
    Spinner,
    Cookie,
    Trash,
} from "@phosphor-icons/react";
import { toast } from "sonner";

export default function AdminTools() {
    const { t } = useT();
    const [info, setInfo] = useState(null);
    const [stats, setStats] = useState(null);
    const [backupBusy, setBackupBusy] = useState(false);
    const [updateBusy, setUpdateBusy] = useState(false);
    const [zip, setZip] = useState(null);
    const [cookiesFile, setCookiesFile] = useState(null);
    const [cookiesBusy, setCookiesBusy] = useState(false);
    const fileRef = useRef(null);
    const cookiesRef = useRef(null);

    const load = async () => {
        try {
            const [a, b] = await Promise.all([api.get("/admin/info"), api.get("/stats")]);
            setInfo(a.data);
            setStats(b.data);
        } catch (e) {
            toast.error(fmtErr(e.response?.data?.detail) || "Eroare");
        }
    };
    useEffect(() => { load(); }, []);

    const doBackup = async () => {
        setBackupBusy(true);
        try {
            const tok = getToken();
            const res = await fetch(`${API}/admin/backup`, {
                method: "GET",
                headers: { Authorization: `Bearer ${tok}` },
                credentials: "include",
            });
            if (!res.ok) {
                let msg = `HTTP ${res.status}`;
                try { msg = (await res.clone().text()) || msg; } catch {}
                throw new Error(msg);
            }
            const blob = await res.blob();
            const cd = res.headers.get("content-disposition") || "";
            const m = cd.match(/filename="([^"]+)"/);
            const fname = m ? m[1] : `backup-${Date.now()}.zip`;
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = fname;
            document.body.appendChild(a); a.click(); document.body.removeChild(a);
            URL.revokeObjectURL(url);
            toast.success(t("admin_backup_btn"));
        } catch (e) {
            toast.error(e.message || "backup failed");
        } finally {
            setBackupBusy(false);
        }
    };

    const doUpdate = async () => {
        if (!zip) return;
        if (!window.confirm(t("admin_update_warn"))) return;
        setUpdateBusy(true);
        try {
            const form = new FormData();
            form.append("file", zip);
            form.append("restart", "true");
            const { data } = await api.post("/admin/update", form, { timeout: 0, maxBodyLength: Infinity });
            toast.success(t("admin_update_ok", { n: data.files_applied }));
            setZip(null);
            if (fileRef.current) fileRef.current.value = "";
        } catch (e) {
            toast.error(fmtErr(e.response?.data?.detail) || e.message);
        } finally {
            setUpdateBusy(false);
        }
    };

    const uploadCookies = async () => {
        if (!cookiesFile) return;
        setCookiesBusy(true);
        try {
            const form = new FormData();
            form.append("file", cookiesFile);
            await api.post("/admin/cookies", form);
            toast.success(t("admin_cookies_uploaded"));
            setCookiesFile(null);
            if (cookiesRef.current) cookiesRef.current.value = "";
            load();
        } catch (e) {
            toast.error(fmtErr(e.response?.data?.detail) || e.message);
        } finally {
            setCookiesBusy(false);
        }
    };

    const deleteCookies = async () => {
        if (!window.confirm(t("confirm"))) return;
        try {
            await api.delete("/admin/cookies");
            toast.success(t("admin_cookies_deleted"));
            load();
        } catch (e) {
            toast.error(fmtErr(e.response?.data?.detail) || e.message);
        }
    };

    return (
        <div className="space-y-8">
            <div>
                <div className="label-mono mb-2">{t("admin_pre")}</div>
                <h1 className="heading-tight fluid-h1">{t("admin_h1")}</h1>
            </div>

            {/* stats */}
            <section className="border p-4 sm:p-6" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
                <div className="label-mono mb-4 flex items-center gap-2"><HardDrives size={14} /> {t("admin_stats_title")}</div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                    <Stat icon={ImagesSquare} label={t("admin_count_images")} value={stats?.images ?? "—"} testid="stat-images" />
                    <Stat icon={FilmStrip} label={t("admin_count_videos")} value={stats?.videos ?? "—"} testid="stat-videos" />
                    <Stat icon={QrCode} label={t("admin_count_qrs")} value={stats?.qrcodes ?? "—"} testid="stat-qrs" />
                    <Stat icon={UploadSimple} label={t("admin_max_upload")} value={info ? `${info.max_upload_gb} GB` : "—"} testid="stat-cap" />
                </div>
                {stats && (
                    <div>
                        <div className="flex justify-between text-xs font-mono mb-1.5" style={{ color: "var(--text-muted)" }}>
                            <span>{t("admin_disk_used")}: {bytesToHuman(stats.used_bytes)}</span>
                            <span>{t("admin_disk_free")}: {bytesToHuman(stats.disk_free)}</span>
                            <span>{t("admin_disk_total")}: {bytesToHuman(stats.disk_total)}</span>
                        </div>
                        <div className="h-1.5 w-full" style={{ background: "var(--surface-2)" }}>
                            <div
                                className="h-1.5"
                                style={{
                                    width: `${Math.min(100, ((stats.disk_total - stats.disk_free) / stats.disk_total) * 100)}%`,
                                    background: "var(--text)",
                                }}
                            />
                        </div>
                    </div>
                )}
            </section>

            {/* cookies */}
            <section className="border p-4 sm:p-6" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
                <div className="label-mono mb-2 flex items-center gap-2"><Cookie size={14} /> cookies</div>
                <h2 className="heading-tight text-2xl mb-2">{t("admin_cookies_title")}</h2>
                <p className="text-sm mb-4" style={{ color: "var(--text-muted)" }}>{t("admin_cookies_desc")}</p>
                <div className="flex items-center gap-2 mb-4 text-xs label-mono" data-testid="cookies-status">
                    <span
                        className="w-2 h-2 rounded-full"
                        style={{ background: info?.cookies?.uploaded ? "var(--success)" : "var(--text-dim)" }}
                    />
                    <span>
                        {info?.cookies?.uploaded
                            ? `${t("admin_cookies_status_yes")} · ${(info.cookies.size / 1024).toFixed(1)} KB`
                            : t("admin_cookies_status_no")}
                    </span>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 flex-wrap">
                    <button
                        onClick={() => cookiesRef.current?.click()}
                        data-testid="btn-pick-cookies"
                        className="btn-press h-11 px-4 flex items-center gap-2 border text-sm"
                        style={{ borderColor: "var(--border-strong)", color: "var(--text)" }}
                    >
                        <Cookie size={16} />
                        {cookiesFile ? cookiesFile.name : t("admin_cookies_pick")}
                    </button>
                    <input
                        ref={cookiesRef}
                        type="file"
                        accept=".txt,text/plain"
                        onChange={(e) => setCookiesFile(e.target.files?.[0] || null)}
                        className="hidden"
                        data-testid="input-cookies"
                    />
                    <button
                        onClick={uploadCookies}
                        disabled={!cookiesFile || cookiesBusy}
                        data-testid="btn-upload-cookies"
                        className="btn-press h-11 px-4 flex items-center gap-2 disabled:opacity-50"
                        style={{ background: "var(--text)", color: "var(--bg)" }}
                    >
                        {cookiesBusy ? <Spinner size={16} className="animate-spin" /> : <UploadSimple size={16} />}
                        <span className="text-sm">{t("admin_cookies_upload")}</span>
                    </button>
                    {info?.cookies?.uploaded && (
                        <button
                            onClick={deleteCookies}
                            data-testid="btn-delete-cookies"
                            className="btn-press h-11 px-4 flex items-center gap-2 text-sm"
                            style={{ background: "var(--danger)", color: "#fff" }}
                        >
                            <Trash size={14} /> {t("admin_cookies_delete")}
                        </button>
                    )}
                </div>
            </section>

            {/* backup */}
            <section className="border p-4 sm:p-6" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
                <div className="label-mono mb-2 flex items-center gap-2"><Database size={14} /> backup</div>
                <h2 className="heading-tight text-2xl mb-2">{t("admin_backup_title")}</h2>
                <p className="text-sm mb-5" style={{ color: "var(--text-muted)" }}>{t("admin_backup_desc")}</p>
                <button
                    onClick={doBackup}
                    disabled={backupBusy}
                    data-testid="btn-backup"
                    className="btn-press h-12 px-5 flex items-center gap-2 disabled:opacity-50"
                    style={{ background: "var(--text)", color: "var(--bg)" }}
                >
                    {backupBusy ? <Spinner size={18} className="animate-spin" /> : <DownloadSimple size={18} />}
                    <span className="text-sm font-medium">{backupBusy ? t("admin_backup_busy") : t("admin_backup_btn")}</span>
                </button>
            </section>

            {/* update */}
            <section className="border p-4 sm:p-6" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
                <div className="label-mono mb-2 flex items-center gap-2"><UploadSimple size={14} /> update</div>
                <h2 className="heading-tight text-2xl mb-2">{t("admin_update_title")}</h2>
                <p className="text-sm mb-3" style={{ color: "var(--text-muted)" }}>{t("admin_update_desc")}</p>
                <div className="border p-3 flex items-center gap-2 mb-4" style={{ borderColor: "var(--warning)", color: "var(--warning)" }}>
                    <Warning size={16} />
                    <span className="text-xs">{t("admin_update_warn")}</span>
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                    <button
                        onClick={() => fileRef.current?.click()}
                        data-testid="btn-pick-update"
                        className="btn-press h-12 px-4 flex items-center gap-2 border text-sm"
                        style={{ borderColor: "var(--border-strong)", color: "var(--text)" }}
                    >
                        <UploadSimple size={16} />
                        {zip ? zip.name : t("admin_update_pick")}
                    </button>
                    <input
                        ref={fileRef}
                        type="file"
                        accept=".zip,application/zip"
                        onChange={(e) => setZip(e.target.files?.[0] || null)}
                        className="hidden"
                        data-testid="input-update-zip"
                    />
                    <button
                        onClick={doUpdate}
                        disabled={!zip || updateBusy}
                        data-testid="btn-apply-update"
                        className="btn-press h-12 px-5 flex items-center gap-2 disabled:opacity-50"
                        style={{ background: "var(--danger)", color: "#fff" }}
                    >
                        {updateBusy ? <Spinner size={18} className="animate-spin" /> : <CheckCircle size={18} />}
                        <span className="text-sm font-medium">{updateBusy ? t("admin_update_busy") : t("admin_update_btn")}</span>
                    </button>
                </div>
            </section>
        </div>
    );
}

function Stat({ icon: Icon, label, value, testid }) {
    return (
        <div className="border p-3" style={{ borderColor: "var(--border)" }} data-testid={testid}>
            <div className="flex items-center gap-2 mb-1" style={{ color: "var(--text-muted)" }}>
                <Icon size={14} />
                <span className="label-mono">{label}</span>
            </div>
            <div className="text-2xl heading-tight">{value}</div>
        </div>
    );
}
